import React, {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import FlexBox from "view/components/Layout/FlexBox";
import {CircularProgress, IconButton, useTheme} from "@material-ui/core";
import AlertIcon from "view/images/AlertIcon";
import {Controller, UseFormReturn} from "react-hook-form";
import {FormContext} from "view/components/Form";
import type {IGridSchemaFilesCell} from "ts/api/grid-schema";
import type {FormAttachmentsResponseCell, GridForm} from "ts/api/task";
import {v4 as uuid} from "uuid";
import {AttachmentFile, RawAttachmentFile} from "ts/api/attachmentFile";
import FileUpload from "view/components/GridResponseBlock/CustomizeTable/Components/Rows/Cell/FilesResponseInput/components/FileUpload";
import {
  flattenFileFormats,
  groupAttachments,
  isAttachmentRaw,
  isValidFileFormat,
  isValueAttachment,
} from "view/components/GridResponseBlock/CustomizeTable/Components/Rows/Cell/FilesResponseInput/utils/helpers";
import CellAttachmentList from "view/components/GridResponseBlock/CustomizeTable/Components/Rows/Cell/FilesResponseInput/components/CellAttachmentList";
import {FileUploadError} from "view/components/GridResponseBlock/CustomizeTable/Components/Rows/Cell/FilesResponseInput/utils/constants";
import handleAttachmentPreview from "tools/file/handleAttachmentPreview";
import bytesToMegaBytes from "tools/file/bytesToMegaBytes";
import {MAX_FILE_PREVIEW_SIZE} from "api/helpers";
import {useFileUploadSizeLimit} from "hooks/file";
import {useTranslate} from "@tolgee/react";
import useStyles from "../styles";

type FileIdToUrl = Record<string, string>;
type AttachmentsFieldUpdate = (value: FormAttachmentsResponseCell) => void;

type FilesInputProps = {
  isRequired: boolean;
  viewOnly?: boolean;
  isJustSavedDraft?: React.MutableRefObject<boolean>;
  columnIndex: number;
  rowIndex: number;
} & Pick<
  IGridSchemaFilesCell,
  "fileTypes" | "maxNumberOfFiles" | "fileSizeLimitMb"
>;

const FilesResponseInput = ({
  isRequired,
  viewOnly,
  rowIndex,
  columnIndex,
  isJustSavedDraft,
  fileTypes: rawFileTypes,
  maxNumberOfFiles: rawMaxNumberOfFiles,
  fileSizeLimitMb: taskFileSizeLimit,
}: FilesInputProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const {t} = useTranslate();
  const fileUrls = useRef<Record<string, string>>({});
  const fileTypes = rawFileTypes ?? [];
  const maxNumberOfFiles = rawMaxNumberOfFiles ?? 2;
  const fileSizeLimit = useFileUploadSizeLimit(taskFileSizeLimit ?? undefined);
  const form = useContext(FormContext) as UseFormReturn<GridForm>;
  const {watch, control, setError, trigger, clearErrors} = form;
  const cellPath = `grid.${rowIndex}.cells.${columnIndex}` as const;

  const attachmentsCell = (watch(cellPath) ||
    {}) as FormAttachmentsResponseCell;
  const {attachments: files = [], isUploading} = attachmentsCell;
  const disabled = (files?.length || 0) >= maxNumberOfFiles || !!viewOnly;
  const supportedFormats = useMemo(() => flattenFileFormats(fileTypes), [
    fileTypes,
  ]);

  const onFileChange = (
    e: ChangeEvent<HTMLInputElement>,
    onChange: AttachmentsFieldUpdate
  ) => {
    const rawFiles = e.target?.files;
    if (!rawFiles?.length) {
      return;
    }
    clearErrors(cellPath);
    const filesArray = Array.from(rawFiles);
    const newFileLength = filesArray.length + files.length;
    if (newFileLength > maxNumberOfFiles) {
      setError(
        cellPath,
        FileUploadError.maxNumFilesExceeded(maxNumberOfFiles, t)
      );
      return;
    }
    const overSizedFile =
      fileSizeLimit &&
      filesArray.find((file) => file.size / 1024 ** 2 > fileSizeLimit);
    if (overSizedFile) {
      setError(
        cellPath,
        FileUploadError.fileSizeExceeded(overSizedFile.name, fileSizeLimit, t)
      );
      return;
    }
    const invalidFormatFile = filesArray.some(
      (file) => !isValidFileFormat(file, supportedFormats)
    );
    if (invalidFormatFile) {
      const extensionNames = supportedFormats.map((format) =>
        format.replace(".", "")
      );
      setError(cellPath, FileUploadError.invalidFileFormat(extensionNames, t));
      return;
    }
    const [processedFilesArray, newIdToFileUrl] = filesArray.reduce<
      [RawAttachmentFile[], FileIdToUrl]
    >(
      (acc, rawFile) => {
        const {size, name} = rawFile;
        const isAlreadyAdded = files.some(
          (file) =>
            file.title === name &&
            isAttachmentRaw(file) &&
            size === file.file.size
        );
        if (isAlreadyAdded) {
          return acc;
        }
        const [attachments, idToFile] = acc;
        const id = uuid();
        const megabytesSize = bytesToMegaBytes(size);
        if (megabytesSize <= MAX_FILE_PREVIEW_SIZE) {
          idToFile[id] = URL.createObjectURL(rawFile);
        }
        attachments.push({title: name, id, file: rawFile});
        return [attachments, idToFile];
      },
      [[], {}]
    );
    onChange({
      type: "attachments",
      attachments: [...files, ...processedFilesArray],
    });
    if (isJustSavedDraft?.current) {
      isJustSavedDraft.current = false;
    }
    const urls = fileUrls.current;
    fileUrls.current = {...urls, ...newIdToFileUrl};

    e.target.value = "";
  };
  const revokeUrls = useCallback(
    () => Object.values(fileUrls.current).forEach(URL.revokeObjectURL),
    []
  );

  const onFileDelete = (
    deleteId: string,
    onChange: AttachmentsFieldUpdate
  ) => () => {
    const {id: fileId} = files.find(({id}) => id === deleteId) || {};
    const objectUrl = fileId ? fileUrls.current[fileId] : null;
    if (fileId && fileUrls.current[fileId]) {
      const {[fileId]: urlToRemove, ...rest} = fileUrls.current;
      fileUrls.current = rest;
    }
    const newAttachments = files.filter(({id}) => id !== deleteId);
    onChange({...attachmentsCell, attachments: newAttachments});
    if (isJustSavedDraft?.current) {
      isJustSavedDraft.current = false;
    }
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
    trigger(cellPath);
  };
  const onPreview = (attachment: AttachmentFile | RawAttachmentFile) => (
    e: MouseEvent
  ) => {
    e.stopPropagation();
    handleAttachmentPreview(attachment, fileUrls.current);
  };
  useEffect(() => {
    return () => revokeUrls();
  }, [revokeUrls]);
  const [rawAttachments, attachments] = useMemo(() => groupAttachments(files), [
    files,
  ]);

  return (
    <Controller
      name={cellPath}
      control={control}
      rules={{
        validate: {
          required: (value) => !isRequired || isValueAttachment(value),
        },
      }}
      render={({field, fieldState}) => {
        const {onChange} = field;
        return (
          <FlexBox flexDirection="column" gap={2} width="100%" padding={0.5}>
            <FlexBox
              alignCenter
              justifyContent="space-between"
              gap={1}
              width="100%"
            >
              <FileUpload
                onChange={(e) => onFileChange(e, onChange)}
                disabled={disabled}
                maxNumberOfFiles={maxNumberOfFiles}
                fileTypes={supportedFormats}
                fileSizeLimit={fileSizeLimit}
                viewMode={viewOnly}
              />
              {!!isUploading && <CircularProgress size={16} />}
              {isRequired && (
                <FlexBox alignCenter gap={0.75}>
                  <IconButton disabled className={classes.cellButton}>
                    <AlertIcon
                      fill={
                        fieldState.error ? theme.palette.error.main : undefined
                      }
                    />
                  </IconButton>
                </FlexBox>
              )}
            </FlexBox>
            <CellAttachmentList
              onDelete={(id) => onFileDelete(id, onChange)}
              attachments={rawAttachments}
              fileUrls={fileUrls.current}
              viewOnly={viewOnly}
              onClick={onPreview}
            />
            <CellAttachmentList
              onDelete={(id) => onFileDelete(id, onChange)}
              attachments={attachments}
              viewOnly={viewOnly}
              onClick={onPreview}
            />
          </FlexBox>
        );
      }}
    />
  );
};

export default FilesResponseInput;
